/**
 * Panel verdicts, as written by the backend (functions/src/verdict.ts). Each
 * expert states a lean on questions with fixed answers; the verdict is the
 * average of their latest leans, computed server-side.
 */

export type AnswerFormatType = "yes_no" | "multiple_choice" | "open_ended";

export interface AnswerFormat {
  type: AnswerFormatType;
  options: string[];
}

/** Option key → share of 100: "yes"/"no", or "A".."E" for multiple choice. */
export type Lean = Record<string, number>;

export interface VerdictOption {
  key: string;
  label: string;
  pct: number;
}

export interface ExpertLean {
  expert: string;
  opening: Lean | null;
  final: Lean;
}

export interface Verdict {
  type: "yes_no" | "multiple_choice";
  options: VerdictOption[];
  winner: string | null;
  experts: ExpertLean[];
  opening: VerdictOption[] | null;
  panelSize: number;
}

export const MIN_CHOICE_OPTIONS = 2;
export const MAX_CHOICE_OPTIONS = 5;
export const MAX_OPTION_LENGTH = 60;
const CHOICE_KEYS = ["A", "B", "C", "D", "E"];

export const OPEN_ENDED: AnswerFormat = { type: "open_ended", options: [] };
export const YES_NO: AnswerFormat = { type: "yes_no", options: ["Yes", "No"] };

export function optionsFor(format: AnswerFormat): Array<{ key: string; label: string }> {
  if (format.type === "yes_no") return [{ key: "yes", label: "Yes" }, { key: "no", label: "No" }];
  if (format.type === "multiple_choice") return format.options.map((label, i) => ({ key: CHOICE_KEYS[i], label }));
  return [];
}

/** Trimmed, de-duplicated choices; null when there aren't 2–5 usable ones. */
export function cleanChoices(options: string[]): string[] | null {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of options) {
    const label = o.replace(/\s+/g, " ").trim().slice(0, MAX_OPTION_LENGTH).trim();
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push(label);
  }
  return out.length >= MIN_CHOICE_OPTIONS && out.length <= MAX_CHOICE_OPTIONS ? out : null;
}

export function parseAnswerFormat(raw: unknown): AnswerFormat | null {
  if (!raw || typeof raw !== "object") return null;
  const { type, options } = raw as { type?: unknown; options?: unknown };
  if (type === "yes_no") return YES_NO;
  if (type === "open_ended") return OPEN_ENDED;
  if (type !== "multiple_choice" || !Array.isArray(options)) return null;
  const choices = cleanChoices(options.filter((o): o is string => typeof o === "string"));
  return choices ? { type: "multiple_choice", options: choices } : OPEN_ENDED;
}

export function sameFormat(a: AnswerFormat | null | undefined, b: AnswerFormat | null | undefined): boolean {
  if (!a || !b) return a === b;
  return a.type === b.type && a.options.join("\u0000") === b.options.join("\u0000");
}

/** Short description for the review step. */
export function formatSummary(format: AnswerFormat): string {
  if (format.type === "yes_no") return "Yes or no";
  if (format.type === "multiple_choice") return `One of: ${format.options.join(", ")}`;
  return "Open answer";
}

const isPct = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100;

function parseLean(raw: unknown): Lean | null {
  if (!raw || typeof raw !== "object") return null;
  const entries = Object.entries(raw as Record<string, unknown>).filter(([, v]) => isPct(v));
  return entries.length > 0 ? (Object.fromEntries(entries) as Lean) : null;
}

/** A verdict read from Firestore, checked field by field; null if malformed. */
export function parseVerdict(raw: unknown): Verdict | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, any>;
  if (v.type !== "yes_no" && v.type !== "multiple_choice") return null;
  if (!Array.isArray(v.options) || v.options.length < 2) return null;
  const options: VerdictOption[] = [];
  for (const o of v.options) {
    if (!o || typeof o.key !== "string" || typeof o.label !== "string" || !isPct(o.pct)) return null;
    options.push({ key: o.key, label: o.label, pct: o.pct });
  }
  const keys = new Set(options.map((o) => o.key));
  const experts: ExpertLean[] = [];
  for (const e of Array.isArray(v.experts) ? v.experts : []) {
    const final = parseLean(e?.final);
    if (typeof e?.expert === "string" && final) experts.push({ expert: e.expert, opening: parseLean(e.opening), final });
  }
  const opening = Array.isArray(v.opening)
    ? v.opening.filter((o: any) => o && keys.has(o.key) && isPct(o.pct)).map((o: any) => ({ key: o.key, label: String(o.label), pct: o.pct }))
    : [];
  return {
    type: v.type,
    options,
    winner: typeof v.winner === "string" && keys.has(v.winner) ? v.winner : null,
    experts,
    opening: opening.length === options.length ? opening : null,
    panelSize: typeof v.panelSize === "number" ? v.panelSize : experts.length,
  };
}

/** The option labels to read leans against: the verdict's, else the format's. */
export function leanOptions(
  verdict: Verdict | null | undefined,
  format: AnswerFormat | null | undefined,
): Array<{ key: string; label: string }> {
  if (verdict) return verdict.options.map(({ key, label }) => ({ key, label }));
  return format ? optionsFor(format) : [];
}

/** "Leans yes · 72%", "Leans Rent · 60%" or "Split 50/50", for one expert's lean. */
export function leanHeadline(lean: Lean, options: Array<{ key: string; label: string }>): string | null {
  const scored = options.filter((o) => typeof lean[o.key] === "number").map((o) => ({ ...o, pct: lean[o.key] }));
  if (scored.length < 2) return null;
  const top = Math.max(...scored.map((o) => o.pct));
  const leaders = scored.filter((o) => o.pct === top);
  if (leaders.length > 1) return `Split ${leaders.map((o) => o.pct).join("/")}`;
  const isYesNo = options.length === 2 && options[0].key === "yes";
  return `Leans ${isYesNo ? leaders[0].label.toLowerCase() : leaders[0].label} · ${top}%`;
}
